import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Archive, DollarSign, Inbox, Loader2, MoreHorizontal, RefreshCw, RotateCcw, Search, Settings, Trash2, X } from "lucide-react";
import { apiGet, apiPatch, apiPost } from "../../utils/apiFetch";
import apiPath from "../../utils/pathObj";
import { isLeadOpened, markLeadOpened } from "./openedLeads";
import { asList } from "./pipedriveHelpers";

// Pipedrive label colour name -> [background, text]
const LABEL_COLORS = {
  blue: ["#dbeafe", "#1d4ed8"], brown: ["#e7d5c9", "#6b4226"], "dark-gray": ["#d1d5db", "#1f2937"],
  gray: ["#e5e7eb", "#4b5563"], green: ["#dcfce7", "#15803d"], orange: ["#ffedd5", "#c2410c"],
  pink: ["#fce7f3", "#be185d"], purple: ["#ede9fe", "#6d28d9"], red: ["#fee2e2", "#b91c1c"], yellow: ["#fef9c3", "#a16207"],
};
const labelColors = (c) => LABEL_COLORS[c] || LABEL_COLORS.gray;

const fmt = (d, opts) => {
  const dt = d && new Date(d);
  return !dt || Number.isNaN(dt.getTime()) ? "—" : dt.toLocaleString("en-US", opts);
};
const DAY = { month: "short", day: "numeric", year: "numeric" };
const fmtDate = (d) => fmt(d, { ...DAY, hour: "numeric", minute: "2-digit" });
const fmtDay = (d) => fmt(d, DAY);

const mapLead = (l) => ({
  id: String(l.id),
  title: l.title || "(Untitled lead)",
  ownerId: l.owner_id,
  nextActivity: l.next_activity_date || (l.next_activity_id ? "Scheduled" : ""),
  addTime: l.add_time || l.created_at,
  updateTime: l.update_time,
  creatorId: l.creator_id,
  expectedClose: l.expected_close_date,
  source: l.source_name || l.origin || "Manually created",
  wasSeen: l.was_seen === true,
  labelIds: l.label_ids || [],
  value: l.value?.amount != null ? `${l.value.currency} ${Number(l.value.amount).toLocaleString()}` : "—",
});

// Table columns. `fixed` columns can't be hidden from the gear menu.
const MUTED = <span className="text-gray-400">—</span>;
const COLUMNS = [
  { k: "title", label: "Title", w: "max-w-[190px]", fixed: true,
    cell: (l) => <span className={l.wasSeen || isLeadOpened(l.id) ? "text-gray-800" : "text-[#c94a5a] font-semibold"}>{l.title}</span> },
  { k: "next", label: "Next activity", w: "",
    cell: (l) => l.nextActivity
      ? <span className="text-gray-700">{fmtDate(l.nextActivity) === "—" ? l.nextActivity : fmtDate(l.nextActivity)}</span>
      : <span className="inline-flex items-center gap-1.5 text-amber-700"><AlertTriangle size={15} className="text-amber-500" /> No activity</span> },
  { k: "labels", label: "Labels", w: "",
    cell: (l, { labelMap }) => l.labelIds.length ? (
      <div className="flex flex-wrap gap-1">
        {l.labelIds.map((id) => {
          const [bg, fg] = labelColors(labelMap[id]?.color);
          return <span key={id} className="px-1.5 py-0.5 rounded text-[11px] font-semibold" style={{ background: bg, color: fg }}>{labelMap[id]?.name || "Label"}</span>;
        })}
      </div>
    ) : MUTED },
  { k: "source", label: "Source origin", w: "",
    // User icon removed (was: <User size={15} className="text-gray-400" />)
    cell: (l) => <span className="text-gray-700">{l.source || "—"}</span> },
  { k: "value", label: "Value", w: "", cell: (l) => <span className="text-gray-700">{l.value}</span> },
  { k: "created", label: "Lead created", w: "", cell: (l) => <span className="text-gray-700 whitespace-nowrap">{fmtDate(l.addTime)}</span> },
  { k: "owner", label: "Owner", w: "", cell: (l, { ownerMap }) => <span className="text-gray-700">{ownerMap[String(l.ownerId)] || (l.ownerId != null ? `User ${l.ownerId}` : "—")}</span> },
  // extra columns: hidden by default, switch them on from the gear menu
  { k: "expected", label: "Expected close", w: "", extra: true, cell: (l) => <span className="text-gray-700">{fmtDay(l.expectedClose)}</span> },
  { k: "updated", label: "Last updated", w: "", extra: true, cell: (l) => <span className="text-gray-700">{fmtDate(l.updateTime)}</span> },
  { k: "creator", label: "Creator", w: "", extra: true, cell: (l, { ownerMap }) => <span className="text-gray-700">{ownerMap[String(l.creatorId)] || (l.creatorId != null ? `User ${l.creatorId}` : "—")}</span> },
  { k: "seen", label: "Seen", w: "", extra: true,
    cell: (l) => <span className="inline-flex items-center gap-1.5 text-gray-700"><span className={`h-2 w-2 rounded-full ${l.wasSeen ? "bg-green-500" : "bg-[#c94a5a]"}`} />{l.wasSeen ? "Seen" : "Unseen"}</span> },
];
const DEFAULT_HIDDEN = COLUMNS.filter((c) => c.extra).map((c) => c.k);
const PREFS_KEY = "pipedrive_lead_table";
const loadPrefs = () => { try { return JSON.parse(localStorage.getItem(PREFS_KEY)) || {}; } catch { return {}; } };

const EMPTY_BULK = { title: "", label_ids: [], owner_id: "", amount: "", currency: "USD", expected_close_date: "" };
const EMPTY_FILTERS = { term: "", label_id: "", source: "", owner_id: "" };
const base = apiPath.pipedriveLeadManager;

const field = "h-9 rounded-md border border-gray-300 bg-white px-2.5 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500";
const iconBtn = "inline-flex h-9 w-9 items-center justify-center rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50";
/** Same joined switcher style as Deals (one border, two boxes inside) */
const viewBtn = "inline-flex h-9 w-9 items-center justify-center text-gray-500 hover:bg-gray-50";
const viewBtnActive = "bg-blue-50 text-blue-600";

function Modal({ title, onClose, footer, children }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button title="Close" onClick={onClose} className="rounded p-1 text-gray-500 hover:bg-gray-100"><X size={18} /></button>
        </div>
        <div className="space-y-4 overflow-y-auto px-5 py-4 text-sm text-gray-700">{children}</div>
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 px-5 py-3">{footer}</div>
      </div>
    </div>
  );
}

function Lead_Inbox() {
  const navigate = useNavigate();
  const [view, setView] = useState("inbox");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [search, setSearch] = useState("");
  const [leads, setLeads] = useState([]);
  const [meta, setMeta] = useState({ labels: [], sources: [], owners: [] });
  const [selected, setSelected] = useState(() => new Set());
  const [hidden, setHidden] = useState(() => new Set(loadPrefs().hidden || DEFAULT_HIDDEN));
  const [compact, setCompact] = useState(() => !!loadPrefs().compact);
  const [menu, setMenu] = useState(null); // row menu: lead id | null
  const [colsOpen, setColsOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulk, setBulk] = useState(EMPTY_BULK);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notConnected, setNotConnected] = useState(false);
  const [, setTick] = useState(0);

  const labelMap = useMemo(() => Object.fromEntries(meta.labels.map((l) => [l.id, l])), [meta.labels]);
  const ownerMap = useMemo(() => Object.fromEntries(meta.owners.map((o) => [String(o.id), o.name])), [meta.owners]);
  const cols = COLUMNS.filter((c) => c.fixed || !hidden.has(c.k));

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNotConnected(false);
    try {
      const params = Object.fromEntries(Object.entries({ view, ...filters }).filter(([, v]) => v));
      setLeads(asList(await apiGet(base, params)).map(mapLead));
    } catch (e) {
      const status = e?.response?.status || e?.status;
      const body = e?.response?.data || {};
      if (status === 401 || status === 403 || body.code === "PIPEDRIVE_NOT_CONNECTED") setNotConnected(true);
      else setError(body.message || e?.message || "Request failed");
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, [view, filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSelected(new Set()); setBulkOpen(false); }, [view, filters]);

  // labels / sources / owners load once
  useEffect(() => {
    apiGet(`${base}/meta`, {}).then((r) => r?.data?.data && setMeta(r.data.data)).catch(() => {});
  }, []);

  // remember column choices and row density
  useEffect(() => {
    try { localStorage.setItem(PREFS_KEY, JSON.stringify({ hidden: [...hidden], compact })); } catch { /* storage unavailable */ }
  }, [hidden, compact]);

  // debounce title search
  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => (f.term === search ? f : { ...f, term: search })), 400);
    return () => clearTimeout(t);
  }, [search]);

  // refresh "opened" colours when returning to the tab
  useEffect(() => {
    const f = () => setTick((t) => t + 1);
    window.addEventListener("focus", f);
    return () => window.removeEventListener("focus", f);
  }, []);

  const setFilter = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));
  const hasFilters = Object.values(filters).some(Boolean);
  const allChecked = leads.length > 0 && selected.size === leads.length;
  const ids = [...selected];
  const flip = (set, v) => { const n = new Set(set); n.has(v) ? n.delete(v) : n.add(v); return n; };

  // one wrapper for every write action
  const act = async (path, list, payload) => {
    setBusy(true);
    setMenu(null);
    try {
      const res = await apiPost(`${base}/${path}`, { ids: list, payload });
      const failed = res?.data?.data?.failed || [];
      if (failed.length) alert(`${failed.length} of ${list.length} failed:\n${failed.map((f) => f.message).filter(Boolean).slice(0, 3).join("\n")}`);
      setSelected(new Set());
      setBulkOpen(false);
      setBulk(EMPTY_BULK);
      await load();
    } catch (e) {
      alert(e?.response?.data?.message || e?.message || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const archive = (list) => act("bulk-update", list, { is_archived: view === "inbox" });
  const remove = (list) => window.confirm(`Delete ${list.length} lead(s)? This cannot be undone.`) && act("bulk-delete", list);
  const convert = (list) => window.confirm(`Convert ${list.length} lead(s) to deals? They'll go to the first stage of your default pipeline.`) && act("bulk-convert", list);

  const submitBulk = () => {
    const payload = {};
    if (bulk.title.trim()) payload.title = bulk.title.trim();
    if (bulk.label_ids.length) payload.label_ids = bulk.label_ids;
    if (bulk.owner_id) payload.owner_id = Number(bulk.owner_id);
    if (bulk.amount !== "") payload.value = { amount: Number(bulk.amount), currency: bulk.currency.trim().toUpperCase() || "USD" };
    if (bulk.expected_close_date) payload.expected_close_date = bulk.expected_close_date;
    if (!Object.keys(payload).length) return alert("Change at least one field");
    act("bulk-update", ids, payload);
  };

  const closeBulk = () => { setBulkOpen(false); setBulk(EMPTY_BULK); };

  const openLead = (lead) => {
    markLeadOpened(lead.id);
    if (!lead.wasSeen) apiPatch(`${apiPath.pipedriveLeads}/${lead.id}`, { was_seen: true }).catch(() => {});
    navigate(`/pipedrive/leads-inbox/${lead.id}`);
  };

  const archiveLabel = view === "inbox" ? "Archive" : "Unarchive";
  const rowActions = (l) => [
    ["Convert to deal", () => convert([l.id])],
    [archiveLabel, () => archive([l.id])],
    ["Delete", () => remove([l.id]), true],
  ];
  const Backdrop = () => <div className="fixed inset-0 z-20" onClick={() => setMenu(null)} />;

  return (
    <div className="flex flex-col h-full min-h-[calc(100vh-64px)] bg-white">
      <header className="px-4 py-3 border-b border-gray-200 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {/* One main div — two boxes inside (like Deals pipeline/list/archive) */}
            <div className="flex border border-gray-300 rounded overflow-hidden bg-white">
              {[["inbox", "Inbox", Inbox], ["archived", "Archive", Archive]].map(([v, title, Ico], i, arr) => (
                <button
                  key={v}
                  type="button"
                  title={title}
                  onClick={() => setView(v)}
                  className={`${viewBtn} ${i < arr.length - 1 ? "border-r border-gray-300" : ""} ${view === v ? viewBtnActive : ""}`}
                >
                  <Ico size={18} />
                </button>
              ))}
            </div>
            {selected.size > 0 && (
              <>
                <span className="mx-1 h-6 border-l border-gray-200" />
                <button title="Convert to deal" disabled={busy} onClick={() => convert(ids)} className={iconBtn}><DollarSign size={18} /></button>
                <button title={archiveLabel} disabled={busy} onClick={() => archive(ids)} className={iconBtn}><Archive size={18} /></button>
                <button title="Delete" disabled={busy} onClick={() => remove(ids)} className={`${iconBtn} hover:!text-red-600`}><Trash2 size={18} /></button>
              </>
            )}
          </div>

          <div className="flex items-center gap-3 text-sm text-gray-600">
            {loading && <Loader2 size={16} className="animate-spin text-[#317e3e]" />}
            <span>{leads.length} {leads.length === 1 ? "lead" : "leads"}</span>
            {selected.size > 0 && (
              <>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-600 px-3 py-1 text-xs font-semibold text-white">
                  {selected.size} SELECTED
                  <button onClick={() => setSelected(new Set())}><X size={13} /></button>
                </span>
                <button className={`${field} font-medium`} onClick={() => setBulkOpen(true)}>Bulk edit</button>
              </>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input className={`${field} w-56 pl-8`} placeholder="Search title..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <select className={`${field} w-40`} value={filters.label_id} onChange={setFilter("label_id")}>
            <option value="">All labels</option>
            {meta.labels.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
          <select className={`${field} w-44`} value={filters.source} onChange={setFilter("source")}>
            <option value="">All sources</option>
            {meta.sources.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
          </select>
          <select className={`${field} w-40`} value={filters.owner_id} onChange={setFilter("owner_id")}>
            <option value="">All owners</option>
            {meta.owners.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
          {hasFilters && (
            <button className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline" onClick={() => { setSearch(""); setFilters(EMPTY_FILTERS); }}>
              <X size={14} /> Clear
            </button>
          )}
        </div>
      </header>

      {notConnected && (
        <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-2 text-sm text-yellow-800 flex justify-between">
          <span>Pipedrive is not connected. Connect it to view leads.</span>
          <a href="/pipedrive-connection" className="font-semibold underline">Go to Integration</a>
        </div>
      )}
      {error && !notConnected && (
        <div className="bg-red-50 border-b border-red-200 px-4 py-2 text-sm text-red-700 flex justify-between">
          <span>Error: {error}</span>
          <button onClick={load} className="font-semibold underline">Retry</button>
        </div>
      )}

      <div className="flex flex-1 min-h-0">
        <div className="flex-1 min-w-0 overflow-x-auto no-scrollbar">
          <table className="w-full min-w-[1100px] table-fixed border-collapse text-left text-sm">
            <thead className="sticky top-0 bg-white z-10">
              <tr className="text-[13px] font-semibold text-[#292d38]">
                <th className="w-10 border-b border-r border-gray-200 px-2.5 py-2.5">
                  <input type="checkbox" className="accent-blue-600" checked={allChecked}
                    onChange={(e) => setSelected(new Set(e.target.checked ? leads.map((l) => l.id) : []))} />
                </th>
                {cols.map((c) => (
                  <th key={c.k} className="whitespace-nowrap border-b border-r border-gray-200 px-2.5 py-2.5">
                    {c.label}
                  </th>
                ))}
                <th className="relative w-12 border-b border-gray-200 px-1.5 py-2.5 text-center">
                  <button title="Table settings" onClick={() => setColsOpen(true)} className="rounded p-1 text-gray-500 hover:bg-gray-100">
                    <Settings size={18} />
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {!loading && !leads.length && !notConnected && !error && (
                <tr>
                  <td colSpan={cols.length + 2} className="border-b border-gray-200 px-4 py-16 text-center text-gray-500">
                    No leads found.
                  </td>
                </tr>
              )}
              {leads.map((l) => (
                <tr
                  key={l.id}
                  onClick={() => openLead(l)}
                  className={`${compact ? "h-9" : "h-[42px]"} cursor-pointer hover:bg-gray-50 ${selected.has(l.id) ? "bg-blue-50" : ""}`}
                >
                  <td className="border-b border-r border-gray-200 px-2.5" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" className="accent-blue-600" checked={selected.has(l.id)} onChange={() => setSelected(flip(selected, l.id))} />
                  </td>
                  {cols.map((c) => (
                    <td key={c.k} className={`overflow-hidden text-ellipsis whitespace-nowrap border-b border-r border-gray-200 px-2.5 py-1.5 ${c.w}`}>
                      {c.cell(l, { labelMap, ownerMap })}
                    </td>
                  ))}
                  <td className="relative border-b border-gray-200 px-1.5 text-center" onClick={(e) => e.stopPropagation()}>
                    <button className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" onClick={() => setMenu(menu === l.id ? null : l.id)}>
                      <MoreHorizontal size={18} />
                    </button>
                    {menu === l.id && (
                      <>
                        <Backdrop />
                        <div className="absolute right-2 top-10 z-30 w-44 rounded-xl border border-gray-100 bg-white py-1.5 text-left shadow-lg">
                          {rowActions(l).map(([label, fn, danger]) => (
                            <div key={label}>
                              {danger && <div className="my-1 border-t border-gray-200" />}
                              <button disabled={busy} onClick={fn} className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${danger ? "text-red-600" : "text-gray-800"}`}>{label}</button>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; width: 0; height: 0; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {colsOpen && (
        <Modal title="Table settings" onClose={() => setColsOpen(false)}
          footer={
            <>
              <div className="flex flex-wrap gap-2">
                <button className={`${field} font-medium`} onClick={() => setHidden(new Set())}>Show all</button>
                <button className={`${field} inline-flex items-center gap-1.5 font-medium`} onClick={() => setHidden(new Set(DEFAULT_HIDDEN))}><RotateCcw size={14} /> Reset</button>
                <button className={`${field} inline-flex items-center gap-1.5 font-medium`} onClick={load}><RefreshCw size={14} /> Refresh</button>
              </div>
              <button onClick={() => setColsOpen(false)} className="h-9 rounded-md bg-[#317e3e] px-5 text-sm font-semibold text-white hover:bg-[#2a6d35]">Done</button>
            </>
          }>
          <div>
            <p className="mb-2 text-xs font-semibold uppercase text-gray-400">Columns</p>
            <div className="grid grid-cols-2 gap-1">
              {COLUMNS.map((c) => (
                <label key={c.k} className={`flex items-center gap-2 rounded px-2 py-1.5 ${c.fixed ? "text-gray-400" : "cursor-pointer hover:bg-gray-50"}`}>
                  <input type="checkbox" className="accent-blue-600" disabled={c.fixed} checked={!hidden.has(c.k)} onChange={() => setHidden(flip(hidden, c.k))} />
                  {c.label}
                </label>
              ))}
            </div>
          </div>
          <div>
            <p className="mb-2 text-xs font-semibold uppercase text-gray-400">Table</p>
            <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-gray-50">
              <input type="checkbox" className="accent-blue-600" checked={compact} onChange={() => setCompact((v) => !v)} />
              Compact rows
            </label>
          </div>
        </Modal>
      )}

      {bulkOpen && selected.size > 0 && (
        <Modal title={`Bulk edit ${selected.size} lead${selected.size > 1 ? "s" : ""}`} onClose={closeBulk}
          footer={
            <>
              <span />
              <div className="flex gap-2">
                <button className={`${field} font-medium`} onClick={closeBulk}>Cancel</button>
                <button disabled={busy} onClick={submitBulk} className="h-9 rounded-md bg-[#317e3e] px-5 text-sm font-semibold text-white hover:bg-[#2a6d35] disabled:opacity-60">
                  {busy ? "Updating..." : "Update"}
                </button>
              </div>
            </>
          }>
          <p className="text-xs text-gray-500">Leave a field empty to keep its current value.</p>
          <label className="block">Title
            <input className={`${field} mt-1 w-full`} placeholder="Keep current value" value={bulk.title} onChange={(e) => setBulk({ ...bulk, title: e.target.value })} />
          </label>
          <div>Labels <span className="text-xs text-gray-400">(replaces existing)</span>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {meta.labels.map((l) => {
                const on = bulk.label_ids.includes(l.id);
                const [bg, fg] = labelColors(l.color);
                return (
                  <button key={l.id} type="button" className="rounded border-2 px-2.5 py-0.5 text-xs font-semibold" style={{ background: bg, color: fg, borderColor: on ? fg : "transparent" }}
                    onClick={() => setBulk({ ...bulk, label_ids: on ? bulk.label_ids.filter((x) => x !== l.id) : [...bulk.label_ids, l.id] })}>{l.name}</button>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <label className="block">Owner
              <select className={`${field} mt-1 w-full`} value={bulk.owner_id} onChange={(e) => setBulk({ ...bulk, owner_id: e.target.value })}>
                <option value="">Keep current value</option>
                {meta.owners.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </label>
            <label className="block">Expected close date
              <input type="date" className={`${field} mt-1 w-full`} value={bulk.expected_close_date} onChange={(e) => setBulk({ ...bulk, expected_close_date: e.target.value })} />
            </label>
          </div>
          <div>Value
            <div className="mt-1 flex gap-2">
              <input type="number" min="0" className={`${field} min-w-0 flex-1`} placeholder="Keep current value" value={bulk.amount} onChange={(e) => setBulk({ ...bulk, amount: e.target.value })} />
              <input className={`${field} w-20 uppercase`} maxLength={3} value={bulk.currency} onChange={(e) => setBulk({ ...bulk, currency: e.target.value })} />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default Lead_Inbox;